import {
  isTimestampInRange,
  rangeLastNDaysInclusive,
  startOfLocalDay,
} from './dashboardDateRange.js';
import {
  ORDER_STATUS_VALUES,
  orderIsPaidViaOnlineGateway,
  orderOnlinePaymentEffectiveState,
  orderStatusLabelPtBr,
} from './orderStatus.js';

/** @typedef {'all' | 'pending' | 'paid' | 'sent_to_erp' | 'cancelled'} OrderStatusFilterId */

/** @typedef {'all' | 'today' | '7' | '30'} OrderPeriodFilterId */

/**
 * @typedef {'all' | 'paid_online' | 'awaiting_mp' | 'mp_issue' | 'offline'} OrderOnlinePayFilterId
 */

/** @typedef {{ status: OrderStatusFilterId, query: string, period: OrderPeriodFilterId, onlinePay: OrderOnlinePayFilterId, paymentMethod: string }} OrderListFilters */

export const ORDER_PERIOD_FILTER_OPTIONS = [
  { id: 'all', label: 'Qualquer data' },
  { id: 'today', label: 'Hoje' },
  { id: '7', label: 'Últimos 7 dias' },
  { id: '30', label: 'Últimos 30 dias' },
];

export const ORDER_ONLINE_PAY_FILTER_OPTIONS = [
  { id: 'all', label: 'Pagamento (todos)' },
  { id: 'paid_online', label: '$ Online confirmado' },
  { id: 'awaiting_mp', label: 'Aguardando MP' },
  { id: 'mp_issue', label: 'MP expirou / recusado' },
  { id: 'offline', label: 'Sem pagamento online' },
];

export const ORDER_STATUS_FILTER_OPTIONS = [
  { id: 'all', label: 'Todos os estados' },
  ...ORDER_STATUS_VALUES.map((id) => ({ id, label: orderStatusLabelPtBr(id) })),
];

/** @returns {OrderListFilters} */
export function createDefaultOrderListFilters() {
  return {
    status: 'all',
    query: '',
    period: 'all',
    onlinePay: 'all',
    paymentMethod: 'all',
  };
}

/** @param {OrderListFilters} filters */
export function orderListFiltersAreActive(filters) {
  return (
    filters.status !== 'all' ||
    filters.query.trim() !== '' ||
    filters.period !== 'all' ||
    filters.onlinePay !== 'all' ||
    filters.paymentMethod !== 'all'
  );
}

/**
 * @param {Record<string, unknown> | null | undefined} order
 * @returns {boolean}
 */
function orderMatchesOnlinePayFilter(order, onlinePay) {
  if (onlinePay === 'all') return true;
  const eff = orderOnlinePaymentEffectiveState(order);
  const paid = orderIsPaidViaOnlineGateway(order);
  const pref =
    typeof order?.mp_preference_id === 'string' ? order.mp_preference_id.trim() : '';

  switch (onlinePay) {
    case 'paid_online':
      return paid;
    case 'awaiting_mp':
      return eff === 'awaiting_gateway';
    case 'mp_issue':
      return eff === 'expired' || eff === 'failed';
    case 'offline':
      return !paid && eff === '' && pref === '';
    default:
      return true;
  }
}

/**
 * @param {Record<string, unknown> | null | undefined} order
 * @param {OrderPeriodFilterId} period
 */
function orderMatchesPeriodFilter(order, period) {
  if (period === 'all') return true;
  const raw = String(order?.created_at || '').trim();
  if (!raw) return false;

  if (period === 'today') {
    const t = new Date(raw.replace(' ', 'T'));
    if (Number.isNaN(t.getTime())) return false;
    return startOfLocalDay(t).getTime() === startOfLocalDay(new Date()).getTime();
  }

  const days = period === '7' ? 7 : 30;
  const { from, to } = rangeLastNDaysInclusive(days);
  return isTimestampInRange(raw, from, to);
}

/**
 * @param {Record<string, unknown> | null | undefined} order
 * @param {string} query
 */
function orderMatchesSearchQuery(order, query) {
  const q = query.trim().toLowerCase();
  if (!q) return true;

  const id = String(order?.id ?? '');
  if (/^\d+$/.test(q) && id.includes(q)) return true;

  const nome = String(order?.customer_nome || '').toLowerCase();
  const email = String(order?.email || order?.customer_email || '').toLowerCase();
  const tel = String(order?.telefone || order?.customer_telefone || '').replace(/\D/g, '');
  const qDigits = q.replace(/\D/g, '');
  const pay = String(order?.payment_method_code || '').toLowerCase();

  if (nome.includes(q)) return true;
  if (email.includes(q)) return true;
  if (qDigits.length >= 4 && tel.includes(qDigits)) return true;
  if (pay.includes(q)) return true;
  if (`#${id}`.includes(q) || `pedido ${id}`.includes(q)) return true;

  return false;
}

/**
 * @param {Record<string, unknown>} order
 * @param {OrderListFilters} filters
 */
export function orderMatchesListFilters(order, filters) {
  const status = String(order?.status || 'pending');
  if (filters.status !== 'all' && status !== filters.status) return false;
  if (!orderMatchesSearchQuery(order, filters.query)) return false;
  if (!orderMatchesPeriodFilter(order, filters.period)) return false;
  if (!orderMatchesOnlinePayFilter(order, filters.onlinePay)) return false;
  if (filters.paymentMethod !== 'all') {
    const code = String(order?.payment_method_code || '').trim();
    if (code !== filters.paymentMethod) return false;
  }
  return true;
}

/**
 * @param {readonly Record<string, unknown>[]} orders
 * @param {OrderListFilters} filters
 * @param {{ excludeStatus?: boolean }} [opts]
 */
export function filterOrdersList(orders, filters, opts = {}) {
  const partial = { ...filters, status: opts.excludeStatus ? 'all' : filters.status };
  return orders.filter((o) => orderMatchesListFilters(o, partial));
}

/**
 * @param {readonly Record<string, unknown>[]} orders
 * @param {OrderListFilters} filters
 */
export function buildOrderFilterStats(orders, filters) {
  const filtered = filterOrdersList(orders, filters);
  const forStatusCounts = filterOrdersList(orders, filters, { excludeStatus: true });

  /** @type {Record<string, number>} */
  const statusCounts = { all: forStatusCounts.length };
  for (const id of ORDER_STATUS_VALUES) {
    statusCounts[id] = forStatusCounts.filter((o) => String(o.status || 'pending') === id).length;
  }

  const paymentMethods = new Set();
  for (const o of orders) {
    const code = String(o?.payment_method_code || '').trim();
    if (code) paymentMethods.add(code);
  }

  return {
    filtered,
    total: orders.length,
    statusCounts,
    paymentMethods: [...paymentMethods].sort((a, b) => a.localeCompare(b, 'pt-BR')),
  };
}
